import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "~/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[var(--radius-sm)] font-medium cursor-pointer transition-colors duration-150 ease-[var(--ease)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--btn-primary-bg)] text-white hover:bg-[var(--btn-primary-bg-hover)] active:bg-[var(--btn-primary-bg-active)] disabled:bg-[var(--btn-primary-bg-disabled)] disabled:text-white/60 disabled:cursor-not-allowed",
        outline:
          "bg-white text-[var(--btn-outline-fg)] border border-[var(--btn-outline-border)] hover:bg-[var(--btn-outline-bg-hover)] disabled:border-border-default disabled:bg-white disabled:text-fg-3 disabled:cursor-not-allowed",
        ghost:
          "bg-transparent text-[var(--btn-text-fg)] hover:text-[var(--btn-text-fg-hover)] hover:underline disabled:text-fg-3 disabled:no-underline disabled:cursor-not-allowed",
        success:
          "bg-[var(--score-a)] text-white hover:brightness-95 active:brightness-90 disabled:bg-[var(--score-a)] disabled:text-white/60 disabled:brightness-100 disabled:cursor-not-allowed",
        "danger-outline":
          "bg-white text-[var(--score-c)] border border-[var(--score-c-bg)] hover:bg-[var(--score-c-bg)] disabled:bg-white disabled:border-border-default disabled:text-fg-3 disabled:cursor-not-allowed",
      },
      size: {
        default: "h-8 px-3.5 text-[14px]",
        sm: "h-7 px-2.5 text-[12px]",
        lg: "h-10 px-4.5 text-[15px]",
        icon: "h-8 w-8 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type = "button", ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...(asChild ? {} : { type })}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
